/** コース進捗・順位・通過タイム差（docs/03_simulation.md 10 章） */
import type { Vec2 } from '../../core/vec';
import type { AthleteState, Standing } from '../types';

export interface Course {
  /** スタート、CP1..n、ゴールの位置 */
  points: Vec2[];
  nodeIds: number[];
  /** points[k] までの累積の長さ */
  cum: number[];
  totalM: number;
}

export function makeCourse(points: Vec2[], nodeIds: number[]): Course {
  const cum = [0];
  for (let k = 1; k < points.length; k++) {
    const a = points[k - 1] as Vec2;
    const b = points[k] as Vec2;
    cum.push((cum[k - 1] as number) + Math.hypot(b.x - a.x, b.z - a.z));
  }
  return { points, nodeIds, cum, totalM: cum[cum.length - 1] as number };
}

export function progressOf(course: Course, a: Pick<AthleteState, 'nextCp' | 'pos' | 'finishTimeS'>): number {
  if (a.finishTimeS !== null) return course.totalM;
  const k = Math.min(a.nextCp, course.points.length - 1);
  const p = course.points[k] as Vec2;
  return Math.max(
    course.cum[k - 1] ?? 0,
    (course.cum[k] as number) - Math.hypot(a.pos.x - p.x, a.pos.z - p.z),
  );
}

/** leader が進捗 p に最初に達した activeRaceTime（記録がなければ null） */
function timeAtProgress(leader: AthleteState, p: number): number | null {
  const log = leader.progressLog;
  const n = log.length / 2;
  let lo = 0;
  let hi = n - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((log[mid * 2] as number) >= p) {
      found = mid;
      hi = mid - 1;
    } else lo = mid + 1;
  }
  return found >= 0 ? (log[found * 2 + 1] as number) : null;
}

export function computeStandings(course: Course, athletes: AthleteState[], nowS: number): Standing[] {
  const sorted = [...athletes].sort((a, b) => {
    if (a.finishTimeS !== null && b.finishTimeS !== null) return a.finishTimeS - b.finishTimeS;
    if (a.finishTimeS !== null) return -1;
    if (b.finishTimeS !== null) return 1;
    return b.progressM - a.progressM;
  });
  const leader = sorted[0] as AthleteState;
  return sorted.map((a, i) => {
    let gapS: number | null = 0;
    if (i > 0) {
      if (a.finishTimeS !== null) gapS = a.finishTimeS - (leader.finishTimeS ?? a.finishTimeS);
      else {
        const t = timeAtProgress(leader, a.bestProgressM);
        gapS = t === null ? null : Math.max(0, nowS - t);
      }
    }
    return {
      athleteId: a.id,
      rank: i + 1,
      progressM: a.progressM,
      remainingM: course.totalM - a.progressM,
      finished: a.finishTimeS !== null,
      gapS,
    };
  });
}
